import {
  IconButton,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Grid,
  Text,
  useDisclosure,
  useColorModeValue,
  Button,
  HStack,
  Tooltip,
  Box,
  Link,
  Flex,
  GridItem,
  Divider,
  Badge,
  Circle,
  useToast,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, RepeatIcon } from '@chakra-ui/icons';
import { CustomChevronLeftIcon } from '../../../theme/icons/CustomChevronLeftIcon';
import MoreInfoLine from '../../common/MoreInfoLine';
import { useContext, useState } from 'react';
import RefreshKeyModal from '../../modals/RefreshKeyModal';
import { CustomRefreshIcon } from '../../../theme/icons/CustomRefreshIcon';
import { CustomVisibilityOffIcon } from '../../../theme/multicolor-icons/CustomVisibilityOffIcon';
import { CustomVisibilityIcon } from '../../../theme/multicolor-icons/CustomVisibilityIcon';
import WebhookSubscriptionModal from '../../modals/WebhookSubscriptionModal';
import DeleteWebhookModal from '../../modals/DeleteWebhookModal';
import { HttpContext } from '../../../context/HttpContext';
import CustomToast from '../../../common/CustomToast';

export default function WebhookMoreInfo({ cell, fetchData }) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const boxBg = useColorModeValue('white', 'secondaryDark');
  const textBg = useColorModeValue('white', 'primaryDark06');
  const labelBg = useColorModeValue('white', 'primaryDark03');
  const borderColor = useColorModeValue('primaryBackground', 'primaryDark');
  const [isKeyVisible, setIsKeyVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { authAxios } = useContext(HttpContext);
  const toast = useToast();

  const {
    isOpen: isRefreshKeyModalOpen,
    onOpen: onRefreshKeyModalOpen,
    onClose: onRefreshKeyModalClose,
  } = useDisclosure();

  const {
    isOpen: isWebhookSubscriptionOpen,
    onOpen: onWebhookSubscriptionOpen,
    onClose: onWebhookSubscriptionClose,
  } = useDisclosure();

  const {
    isOpen: isDeleteWebhookModalOpen,
    onOpen: onDeleteWebhookModalOpen,
    onClose: onDeleteWebhookModalClose,
  } = useDisclosure();

  const subscription = cell.row.original;

  const handleClick = (e) => {
    e.stopPropagation();
    onOpen();
  };

  const handleTestWebhook = async () => {
    try {
      await authAxios.post(
        `/api/v1/webhook-subscriptions/${subscription.id}/test`
      );
      toast({
        render: ({ onClose }) => (
          <CustomToast
            status="success"
            title="Test webhook sent"
            onClose={onClose}
          />
        ),
      });
    } catch (onError) {
      toast({
        render: ({ onClose }) => (
          <CustomToast
            status="error"
            title={`Something went wrong`}
            description={'Please try again later'}
            onClose={onClose}
          />
        ),
      });
    }
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        icon={
          <CustomChevronLeftIcon
            w="20px"
            h="20px"
            color="white"
            transform="rotate(180deg)"
          />
        }
        isRound={true}
        size="sm"
        w="36px"
        mr={1}
        aria-label="More info"
      />

      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
        <DrawerOverlay />
        <DrawerContent bg={boxBg}>
          <DrawerCloseButton />
          <DrawerHeader
            borderBottomWidth="1px"
            borderBottomColor={borderColor}
            pt={{ base: '16px', lg: '56px' }}
            pb={3}
            bg={textBg}
          >
            Webhook subscription details
          </DrawerHeader>

          <DrawerBody p={0} bg={labelBg}>
            <Grid
              templateColumns={{ base: '150px 1fr', md: '192px 1fr' }}
              gap={0}
            >
              <MoreInfoLine heading="ID" value={subscription.id} />
              <MoreInfoLine heading="Target URL" value={subscription.url} />
              <MoreInfoLine
                heading="Enabled"
                value={
                  subscription.enabled ? (
                    <HStack>
                      <Circle size="15px" bg="success" />
                      <Text>Yes</Text>
                    </HStack>
                  ) : (
                    <HStack>
                      <Circle size="15px" bg="removed" />
                      <Text>No</Text>
                    </HStack>
                  )
                }
              />

              <MoreInfoLine
                heading="Scope"
                value={
                  subscription.scopeType === 'entity'
                    ? subscription.scopeName
                    : 'Org‑wide'
                }
              />

              <MoreInfoLine
                heading="Subscribed events"
                value={
                  <HStack gap={2} flexWrap="wrap">
                    {subscription.events.map((event) => (
                      <Badge key={event.name} variant="removed" px={3} py={1.5}>
                        {event}
                      </Badge>
                    ))}
                  </HStack>
                }
              />
              <MoreInfoLine
                heading={
                  <Flex direction="column" gap={2} alignItems="flex-start">
                    Secret key
                    <Box onClick={onRefreshKeyModalOpen} cursor={'pointer'}>
                      <Flex
                        justifyContent={{ base: 'start', md: 'center' }}
                        alignItems="center"
                      >
                        <CustomRefreshIcon boxSize="14px" />
                        <Text pl="7px" pt="1px" color={'#EC675A'}>
                          Refresh
                        </Text>
                      </Flex>
                    </Box>
                  </Flex>
                }
                value={
                  <Flex alignItems="center">
                    {isKeyVisible ? (
                      <CustomVisibilityOffIcon
                        w="16px"
                        h="16px"
                        mr="10px"
                        cursor="pointer"
                        onClick={() => setIsKeyVisible(false)}
                      />
                    ) : (
                      <CustomVisibilityIcon
                        w="16px"
                        h="16px"
                        mr="10px"
                        cursor="pointer"
                        onClick={() => setIsKeyVisible(true)}
                      />
                    )}
                    <Text
                      whiteSpace="normal"
                      overflow="hidden"
                      wordBreak="break-word"
                    >
                      {isKeyVisible
                        ? subscription.secretKey
                        : '•••••••••••••••••••••••••••••'}
                    </Text>
                  </Flex>
                }
              />
              <MoreInfoLine
                heading="Manage"
                value={
                  <Flex gap={2} direction="column" alignItems="flex-start">
                    <Button
                      variant="link"
                      color="primaryDark"
                      fontSize="14px"
                      leftIcon={<RepeatIcon />}
                      onClick={handleTestWebhook}
                    >
                      Test Webhook
                    </Button>
                    <Button
                      variant="link"
                      color="primaryDark"
                      fontSize="14px"
                      leftIcon={<EditIcon />}
                      onClick={onWebhookSubscriptionOpen}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="link"
                      color="primaryDark"
                      fontSize="14px"
                      leftIcon={<DeleteIcon />}
                      onClick={onDeleteWebhookModalOpen}
                    >
                      Delete
                    </Button>
                  </Flex>
                }
              />
            </Grid>
          </DrawerBody>

          <DrawerFooter
            borderTopWidth="1px"
            borderTopColor={borderColor}
            bg={textBg}
          >
            <HStack spacing={4} w="full" justifyContent="space-between">
              <Link
                color={useColorModeValue('secondaryDark08', '#DDDDDD')}
                fontSize="14px"
                onClick={onClose}
                mr="30px"
              >
                <Text textStyle="headingFamilyMedium">Back to list</Text>
              </Link>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <RefreshKeyModal
        isOpen={isRefreshKeyModalOpen}
        onClose={onRefreshKeyModalClose}
        webhookSubscriptionId={subscription.id}
        fetchData={fetchData}
      />
      <WebhookSubscriptionModal
        isOpen={isWebhookSubscriptionOpen}
        onClose={onWebhookSubscriptionClose}
        fetchData={fetchData}
        initialData={subscription}
      />
      <DeleteWebhookModal
        isOpen={isDeleteWebhookModalOpen}
        onClose={onDeleteWebhookModalClose}
        fetchData={fetchData}
        webhookSubscriptionId={subscription.id}
      />
    </>
  );
}
